import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';
import { AntDesign } from '@expo/vector-icons';
import { fontFamily } from "../../helpers/fontFamily";
import { LinearGradient } from 'expo-linear-gradient';
import receiveRecountStyles from '../receive_recount/styles';

const PUSH_OPTIONS_DATA = [
  {
    value: 'push_updated_gp',
    label: 'Push Updated GP quantity to Shopify',
    testID: 'pushOptionUpdatedGP',
    tooltipDescription: "The new count including what was received will sync from GroovePacker to Shopify. This will overwrite Shopify's inventory for that item.",
  },
  {
    value: 'push_received_difference',
    label: 'Push Received difference to Shopify',
    testID: 'pushOptionReceivedDiff',
    tooltipDescription: 'The quantity that was received will be added to the quantity currently in Shopify. This is preferred if you only use GroovePacker for receiving and Shopify is your "source of truth".',
  }
];

const PushOptionsDropdown = ({
  shouldShowPushOptionsDropdown,
  isEditing,
  pushOption,
  showPushOptionsDropdown,
  setShowPushOptionsDropdown,
  handlePushOptionChange
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleOptionSelect = useCallback((optionValue) => {
    handlePushOptionChange(optionValue);
    setShowPushOptionsDropdown(false);
  }, [handlePushOptionChange, setShowPushOptionsDropdown]);

  const toggleTooltip = useCallback(() => {
    setShowTooltip(prev => !prev);
  }, []);

  const handleDropdownToggle = useCallback(() => {
    setShowTooltip(false);
    setShowPushOptionsDropdown(!showPushOptionsDropdown);
  }, [showPushOptionsDropdown, setShowPushOptionsDropdown]);

  const closeTooltip = useCallback(() => {
    setShowTooltip(false);
  }, []);

  if (!shouldShowPushOptionsDropdown() || isEditing) {
    return null;
  }

  const getSelectedOptionLabel = () => {
    const selectedOption = PUSH_OPTIONS_DATA.find(option => option.value === pushOption);
    return selectedOption ? selectedOption.label : '';
  };

  return (
    <>
      {showTooltip && (
        <TouchableOpacity
          style={styles.tooltipOverlay}
          onPress={closeTooltip}
          activeOpacity={1}
        />
      )}

      <View style={styles.container}>
        <Text style={styles.label}>
          Choose Push Operation
        </Text>

        <View style={styles.dropdownRow}>
          <View style={styles.dropdownContainer}>
            <TouchableOpacity
              style={receiveRecountStyles.dropDownToggleStyle}
              onPress={handleDropdownToggle}
              testID="pushOperationContainer"
              accessibilityRole="button"
              accessibilityLabel="Toggle push options dropdown"
            >
              <View style={styles.toggleContent}>
                <Text
                  style={styles.selectedText}
                  numberOfLines={1}
                >
                  {getSelectedOptionLabel()}
                </Text>
                <AntDesign
                  name={showPushOptionsDropdown ? 'up' : 'down'}
                  size={16}
                  color="rgb(205,205,205)"
                  style={styles.chevronIcon}
                />
              </View>
            </TouchableOpacity>

            {showPushOptionsDropdown && (
              <View>
                <LinearGradient
                  colors={['#2D445F', '#2D445F']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 0, y: 1 }}
                  style={styles.dropdownContent}
                >
                  {PUSH_OPTIONS_DATA.map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      onPress={() => handleOptionSelect(option.value)}
                      testID={option.testID}
                      style={[
                        styles.optionButton,
                        pushOption === option.value && styles.selectedOption
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel={option.label}
                      accessibilityState={{ selected: pushOption === option.value }}
                    >
                      <Text style={[
                        styles.optionText,
                        pushOption === option.value && styles.selectedOptionText
                      ]}>
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </LinearGradient>
              </View>
            )}
          </View>

          <TouchableOpacity
            style={styles.infoButton}
            onPress={toggleTooltip}
            accessibilityRole="button"
            accessibilityLabel="Show push operation information"
          >
            <AntDesign
              name="infocirlce"
              size={20}
              color="rgb(119,143,165)"
            />

            {showTooltip && (
              <View style={styles.tooltipContainer}>
                <LinearGradient
                  colors={['#90A3B4', '#B5C9DD', '#DCEAFC']}
                  locations={[0, 0.13, 1]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 0, y: 1 }}
                  style={styles.tooltipContent}
                >
                  <View style={styles.tooltipArrow} />

                  {PUSH_OPTIONS_DATA.map((option) => (
                    <View key={option.value} style={styles.tooltipItem}>
                      <Text style={styles.tooltipTitle}>
                        {option.label}:
                      </Text>
                      <Text style={styles.tooltipDescription}>
                        {option.tooltipDescription}
                      </Text>
                    </View>
                  ))}
                </LinearGradient>
              </View>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  tooltipOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  container: {
    width: '95%',
    marginTop: 20,
    maxWidth: 353,
  },
  label: {
    fontFamily: fontFamily.font700,
    fontSize: 12,
    color: '#ffffff',
  },
  dropdownRow: {
    flexDirection: 'row',
    flex: 1,
    position: 'relative',
  },
  dropdownContainer: {
    position: 'relative',
    flex: 1,
  },
  toggleContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  selectedText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: fontFamily.font400,
    lineHeight: 36,
  },
  chevronIcon: {
    marginLeft: 8,
  },
  dropdownContent: {
    position: 'absolute',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#FFFFFF',
    padding: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 5,
    zIndex: 1000,
  },
  optionButton: {
    borderRadius: 4,
    marginVertical: 2,
    height: 40,
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  selectedOption: {
    backgroundColor: '#4A5A72',
  },
  optionText: {
    fontFamily: fontFamily.font400,
    color: '#ffffff',
    fontSize: 16,
    lineHeight: 22,
  },
  selectedOptionText: {
    fontWeight: 'bold',
    color: '#ffffff',
  },
  infoButton: {
    marginLeft: 5,
    position: 'relative',
  },
  tooltipContainer: {
    position: 'absolute',
    bottom: 50,
    left: -150,
    zIndex: 1000,
    width: 320,
    maxWidth: 320,
  },
  tooltipContent: {
    borderRadius: 15,
    borderWidth: 1,
    padding: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    borderColor: '#90A3B4',
  },
  tooltipArrow: {
    position: 'absolute',
    bottom: -8,
    left: 150,
    width: 0,
    height: 0,
    borderLeftWidth: 8,
    borderRightWidth: 8,
    borderTopWidth: 8,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: '#DCEAFC',
  },
  tooltipItem: {
    padding: 8,
  },
  tooltipTitle: {
    color: '#000000',
    fontSize: 12,
    fontFamily: fontFamily.font400,
    lineHeight: 16,
    marginBottom: 2,
  },
  tooltipDescription: {
    color: '#000000',
    fontSize: 12,
    fontFamily: fontFamily.font400,
    lineHeight: 16,
    opacity: 0.8,
  },
});

PushOptionsDropdown.propTypes = {
  shouldShowPushOptionsDropdown: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  pushOption: PropTypes.oneOf(['push_updated_gp', 'push_received_difference']).isRequired,
  showPushOptionsDropdown: PropTypes.bool.isRequired,
  setShowPushOptionsDropdown: PropTypes.func.isRequired,
  handlePushOptionChange: PropTypes.func.isRequired,
};

export default PushOptionsDropdown;